/**
 * MobileFollowupDrawer
 *
 * Mobile replacement for WeeklyFollowupModal.
 * Uses MUI SwipeableDrawer (bottom sheet) to display followup details.
 * Read-only view matching the desktop modal's functionality.
 *
 * Enhanced with futuristic glassmorphic aesthetic.
 */
import React, { useEffect, useState } from "react";
import {
  SwipeableDrawer,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import SaveIcon from "@mui/icons-material/Save";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { getTagClass } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Futuristic style tokens                                            */
/* ------------------------------------------------------------------ */
const glassEasing = "cubic-bezier(0.4, 0, 0.2, 1)";
const entranceEasing = "cubic-bezier(0, 0, 0.2, 1)";
const TRANSITION_SMOOTH = `all 0.25s ${glassEasing}`;

const softShadow = (level = 1) => {
  const shadows = {
    0: "none",
    1: "0 2px 8px rgba(99, 115, 129, 0.08), 0 1px 2px rgba(99, 115, 129, 0.06)",
    2: "0 4px 16px rgba(99, 115, 129, 0.12), 0 2px 4px rgba(99, 115, 129, 0.08)",
    3: "0 8px 32px rgba(99, 115, 129, 0.16), 0 4px 8px rgba(99, 115, 129, 0.1)",
  };
  return shadows[level] || shadows[1];
};

const glassSurface = (opacity = 0.55) => ({
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  backgroundColor: `rgba(255, 255, 255, ${opacity})`,
});

const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

/* ------------------------------------------------------------------ */
/*  Info row component                                                 */
/* ------------------------------------------------------------------ */
const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.25,
      px: 0.5,
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        fontSize: "0.82rem",
        color: "text.secondary",
        letterSpacing: "-0.01em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontSize: "0.88rem",
        fontWeight: 500,
        color: "text.primary",
        letterSpacing: "-0.01em",
      }}
    >
      {value}
    </Typography>
  </Box>
);

/**
 * @param {Object} props
 * @param {Object|null} props.selected - followup data object or null/undefined to hide
 * @param {Function} props.onHide - called to close the drawer
 * @param {number} [props.zIndex] - optional z-index override (needed when this
 *   drawer is opened from inside another MUI modal/drawer, e.g. the collab modal)
 * @param {boolean} [props.editable] - when true, the comment becomes an editable
 *   field with a Save button (matches the desktop WeeklyFollowupTable popup)
 * @param {Function} [props.onSave] - called with the edited comment string on save
 */
export default function MobileFollowupDrawer({
  selected,
  onHide,
  zIndex,
  editable = false,
  onSave,
}) {
  const isOpen = !!selected;

  const [comments, setComments] = useState("");
  const [commentError, setCommentError] = useState(false);

  useEffect(() => {
    setComments(selected?.comments || "");
    setCommentError(false);
  }, [selected]);

  const handleSave = () => {
    if (!comments.trim()) {
      setCommentError(true);
      return;
    }
    if (onSave) onSave(comments);
  };

  const getChipColor = (value) => {
    const severity = getTagClass(value);
    return severityToColor[severity] || "default";
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isOpen}
      onClose={onHide}
      onOpen={() => {}}
      disableSwipeToOpen
      transitionDuration={{ enter: 380, exit: 280 }}
      sx={zIndex != null ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          borderRadius: "24px 24px 0 0",
          maxHeight: "85vh",
          background:
            "linear-gradient(180deg, rgba(245, 247, 250, 0.97) 0%, rgba(255, 255, 255, 0.99) 100%)",
          ...glassSurface(0.92),
          boxShadow: "0 -8px 40px rgba(99, 115, 129, 0.15)",
          overflow: "hidden",
        },
      }}
    >
      {/* Puller handle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: 1.5,
          pb: 0.75,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 5,
            borderRadius: 3,
            background:
              "linear-gradient(90deg, rgba(255,144,21,0.35), rgba(255,193,0,0.35))",
            boxShadow: "0 1px 4px rgba(255,144,21,0.12)",
            transition: TRANSITION_SMOOTH,
          }}
        />
      </Box>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          pb: 1.5,
          pt: 0.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: "1.08rem",
            letterSpacing: "-0.025em",
            color: "text.primary",
            lineHeight: 1.3,
            flex: 1,
            mr: 1,
          }}
        >
          {selected
            ? `${Localize(selected.type)} - ${selected.prenom} ${selected.nom}`
            : ""}
        </Typography>
        <IconButton
          onClick={onHide}
          size="small"
          sx={{
            minWidth: 38,
            minHeight: 38,
            borderRadius: "12px",
            ...glassSurface(0.6),
            border: "1px solid rgba(0,0,0,0.06)",
            boxShadow: softShadow(1),
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              backgroundColor: "rgba(255,144,21,0.08)",
              borderColor: "rgba(255,144,21,0.2)",
              transform: "scale(1.05)",
              boxShadow: `${softShadow(2)}, 0 0 12px rgba(255,144,21,0.1)`,
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        </IconButton>
      </Box>

      <Divider
        sx={{
          opacity: 0.08,
          mx: 2,
          borderColor: "rgba(99, 115, 129, 0.5)",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          px: 2.5,
          py: 2.5,
          overflowY: "auto",
          animation: "fadeSlideIn 380ms cubic-bezier(0, 0, 0.2, 1)",
          "@keyframes fadeSlideIn": {
            "0%": { opacity: 0, transform: "translateY(8px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {/* Week badge */}
        {selected?.week != null && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              mb: 2.5,
              px: 1.75,
              py: 0.75,
              borderRadius: "10px",
              background:
                "linear-gradient(135deg, rgba(255,135,1,0.1) 0%, rgba(255,193,0,0.08) 100%)",
              border: "1px solid rgba(255,144,21,0.15)",
              boxShadow: "0 2px 8px rgba(255,144,21,0.08)",
              transition: TRANSITION_SMOOTH,
            }}
          >
            <CalendarTodayRoundedIcon
              sx={{ fontSize: 15, color: "#FF9015", opacity: 0.8 }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.82rem",
                color: "#FF9015",
                letterSpacing: "-0.01em",
              }}
            >
              <Langs str="Semaine" /> {parseInt(selected.week) + 1}
            </Typography>
          </Box>
        )}

        {/* Comments — editable when allowed, otherwise read-only card */}
        {editable ? (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: "0.72rem",
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                mb: 1,
                display: "block",
                opacity: 0.7,
              }}
            >
              <Langs str="Commentaires" />
            </Typography>
            <TextField
              value={comments}
              onChange={(e) => {
                setComments(e.target.value);
                if (commentError) setCommentError(false);
              }}
              error={commentError}
              multiline
              minRows={6}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  ...glassSurface(0.5),
                  transition: TRANSITION_SMOOTH,
                },
                "& textarea": { fontSize: "16px" },
              }}
            />
          </Box>
        ) : (
          selected?.comments && (
            <Box
              sx={{
                mb: 2.5,
                p: 2.25,
                borderRadius: "16px",
                ...glassSurface(0.5),
                border: "1px solid rgba(0,0,0,0.05)",
                boxShadow: softShadow(1),
                transition: TRANSITION_SMOOTH,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 3,
                  height: "100%",
                  background:
                    "linear-gradient(180deg, #FF8701, #FFC100)",
                  borderRadius: "3px 0 0 3px",
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: 1,
                  display: "block",
                  opacity: 0.7,
                }}
              >
                <Langs str="Commentaires" />
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.88rem",
                  lineHeight: 1.65,
                  whiteSpace: "pre-wrap",
                  color: "text.primary",
                  fontWeight: 400,
                }}
              >
                {selected.comments}
              </Typography>
            </Box>
          )
        )}

        {/* Detail rows card */}
        <Box
          sx={{
            borderRadius: "16px",
            ...glassSurface(0.45),
            border: "1px solid rgba(0,0,0,0.05)",
            boxShadow: softShadow(1),
            overflow: "hidden",
            px: 2,
            py: 0.5,
          }}
        >
          {/* Status */}
          {selected?.status && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1.5,
                  px: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    color: "text.secondary",
                    letterSpacing: "-0.01em",
                    textTransform: "uppercase",
                  }}
                >
                  <Langs str="Status" />
                </Typography>
                <Chip
                  label={<Langs str={selected.status} />}
                  size="small"
                  color={getChipColor(selected.status)}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: 28,
                    borderRadius: "10px",
                    letterSpacing: "-0.01em",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                    transition: TRANSITION_SMOOTH,
                  }}
                />
              </Box>
              <Divider sx={{ opacity: 0.06, mx: -0.5 }} />
            </>
          )}

          {/* Suivi par */}
          {selected?.createdBy && (
            <>
              <InfoRow
                label={<Langs str="Suivi par" />}
                value={selected.createdBy}
              />
              <Divider sx={{ opacity: 0.06, mx: -0.5 }} />
            </>
          )}

          {/* Updated by */}
          {selected?.updatedBy && (
            <>
              <InfoRow
                label={<Langs str="updated_by" />}
                value={selected.updatedBy}
              />
              <Divider sx={{ opacity: 0.06, mx: -0.5 }} />
            </>
          )}

          {/* Updated at */}
          {selected?.updated_at && (
            <InfoRow
              label={<Langs str="updated_at" />}
              value={moment(selected.updated_at).format("DD-MM-YYYY HH:mm")}
            />
          )}
        </Box>

        {/* Save button (editable mode only) */}
        {editable && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            fullWidth
            sx={{
              mt: 2.5,
              borderRadius: "14px",
              textTransform: "none",
              minHeight: 52,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              background:
                "linear-gradient(135deg, #ff9015, rgba(255, 144, 21, 0.9))",
              boxShadow: "0 4px 16px rgba(255, 144, 21, 0.3)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                background:
                  "linear-gradient(135deg, #e67e00, rgba(230, 126, 0, 0.95))",
                boxShadow: "0 6px 20px rgba(255, 144, 21, 0.4)",
              },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            <Langs str="Save" />
          </Button>
        )}

        {/* Bottom spacing */}
        <Box sx={{ height: 28 }} />
      </Box>
    </SwipeableDrawer>
  );
}
